import type { InsuranceParamsType } from '../enums';
import { Country, RiskVariant, Selection } from '../enums';
import type { SourceTypes } from '../enums/SourceTypes';
import { customAddDays } from '../extensions/date.extensions';
import type { Addon } from './Addon';
import type { InsurerDiscount } from './Discounts';
import type { OfferSimple } from './Offer';
import type { BaseAddressDto, PersonAdditionalData, PersonDto } from './Person';
import type { PolicyHolderAdditionalData } from './Step4';
import type { StepBase } from './StepBase';
import type { User } from './User';

export type Vehicle = VehicleIdentificationDto & VehicleDto & VehiclePriceDto

export interface InsuranceAdditionalDatesModel {
  paramsId: number | string
  contractDeliveryDate?: Date | null
  firstPaymentDate?: Date | null
  commissionPaidDate?: Date | null
}

export interface IInsuranceParams {
  policyHolder: PolicyHolderDto
  generalParams: GeneralParamsDto
}

export type InsuranceParamsDto = {
  type?: InsuranceParamsType
  vehicle: Vehicle
  baseParams: BaseParamsVehicleDto
  addons?: Addon[]
  importedData?: PoleasImportedData
} & StepBase &
  IInsuranceParams

export type InsuranceParamsLeasingDto = {
  leasingData?: InsuranceParamsLeasingDataDto
  client?: PersonAdditionalData
} & InsuranceParamsDto

export type AddonBaseParamsDto = {
  addonInsurers: number[] | undefined
  addonVariants?: AddonVariant[] | undefined
}

export type BaseParamsDto = {
  insurers?: number[] | undefined
  insurerDiscounts?: InsurerDiscount[] | undefined
  variants?: Variant[] | undefined
} & AddonBaseParamsDto

export type BaseParamsVehicleDto = {
  pov?: number[] | undefined
  hav?: number[] | undefined
  spol?: number[] | undefined
} & BaseParamsDto

export type AddonVariant = {
  riskVariantId: number
  selected: boolean
}

export interface VariantPart {
  risk: number
  riskVariant: number
}

export interface Variant {
  id: number
  variantParts: VariantPart[] | null
}

export interface InsuranceBase {
  insuranceStartDate?: Date | null
  insuranceEndDate?: Date | null
  contractAcceptationType?: number | null
  contractNumber?: string
  selectedOfferId?: number | null
  insuranceId?: number | null
  contractCreatedBy?: User | null
  contractCreatedDate?: Date | null
  contractCanceled?: boolean | null
  contractCanceledBy?: string
  contractCanceledDate?: Date | null
  paymentDate?: Date | null
  paymentDateSpecial?: string
  variableSymbol?: string
  contractAcceptedDate?: Date | null
  contractEmailSent?: boolean
  digitalSignatureUrl?: string
  paymentUrl?: string
}

export interface InsuranceDto extends InsuranceBase {
  cancellationOptions?: ContractCancellationOptions
}

export interface ContractCancellationOptions {
  canCancel: boolean
  canReactivate: boolean
  isCancelled: boolean
  cancellationDeadline?: Date | null
}

export type GeneralParamsDto = {
  clientId?: string | null
  paramsId?: string | null
  insuranceCategoryType?: number | null
  clonedFromParamsId?: number | null
  paymentFrequency?: number
  paymentType?: number
  name?: string
  createdBy?: User | null
  created?: Date | null
  lastWizardStep?: number | null
  contractReplacementNumber?: string | null
  contractReplacementInsurerId?: number
  paramsTypeId?: InsuranceParamsType
  existsOffers: boolean
  existingOffers?: OfferSimple[] | null
  firstPaymentDate?: Date | null
  commissionPaidDate?: Date | null
  contactEmail?: string
  previousLeasingNumber?: string
  paramsState?: string
  paramsStateText?: string
  source?: SourceTypes

  // pouzivano pro public poleas
  paramsDataValid?: boolean
  paramasDisagreements?: string
  clientDisabled?: boolean
  pastInsuranceEnd?: Date | null
} & InsuranceBase

export type VehicleIdentificationDto = {
  licensePlate?: string | null
  vin?: string | null
}

export type VehicleDto = {
  brokerMakeModelId?: number | null
  druh?: number | null
  subCategory?: number | null
  palivo?: number | null
  znacka?: string | null
  znackaText?: string | null
  zakladniModel?: string | null
  model?: string | null
  vehicleTypeDescription?: string | null
  puvod?: number | null
  uzemniPlatnost?: number | null
  objem?: number | null
  vykon1?: number | null
  vykon2?: number | null
  celkovaHmotnost?: number | null
  pocetMistSezeni?: number | null
  isInProduction?: boolean | null
  firstRegistrationDate?: Date | null
  firstRegistrationDateCz?: Date | null
  mileage?: number | null
  // doNotValidateMileage?: boolean
  cisloTechnickehoPrukazu?: string | null
  cisloOrv?: string | null
  usage?: number | null
  yearKm?: string | null
  // doNotValidateYearKm?: boolean
  serialNumber?: string | null

  purchaseDate?: Date | null
  warrantyStartDate?: Date | null // zaruka - datum pocatku
  warrantyKmLimit?: string | null // zaruka - pocet km
  warrantyTermLimit?: string | null // zaruka - pocet mesicu
  manufacturerWarrantyType?: number | null // vozidlo v záruce výrobce: 1 ... ano, 2 ... ne, 0 ... nechci uvést
  vehicleLeasing: boolean | null
  subaruConditionsFulfilled?: boolean | null
  mazdaConditionsFulfilled?: boolean | null
  kiaConditionsFulfilled?: boolean | null
  smluvniServis: boolean | null
  prefLikvidace: boolean | null
  autorizovanyServis: boolean | null
  foreignRepairShop: boolean | null

  equipment: number[] | null
  extraEquipments: ExtraEquipment[] | null
  security: number[] | null

  vehicleGaraged?: boolean
}

export type ExtraEquipment = {
  id?: number
  name?: string
  price?: number
  purchaseDate?: Date | string
}

export type VehiclePriceDto = {
  price: number | null
  origInsuranceParamsPrice: number | null
  priceType?: number | null
  priceIncludeVat: boolean | null
  useVehicleEvaluation: boolean | null
  cebicatVehicleId?: string | null
}

export type PolicyHolderDto = {
  benefits: string[]
  bigRiskCompany: boolean
  person: PersonAdditionalData
  holderSameAs: number | null
  holder?: PersonAdditionalData | undefined
  ownerSameAs: number | null
  owner?: PersonAdditionalData | undefined
  insuredPersonSameAs: number | null
  insuredPerson?: PersonAdditionalData | undefined

  agreementWarningMessage?: string
  hasVehicleMoreThanOneYear?: boolean // sleva za zkušenost Uniqa AiV
  youngDriverComplicity?: boolean // sleva mladého řidiče Uniqa Unipoint
  csobBaScoring?: boolean
  propojistenost?: boolean
  propojistenostCSOB?: string
  propojistenostSlavia?: string
  propojistenostAxa?: string
}

export interface PoleasImportedData {
  importId?: number
  importedDate?: Date
  leasingNumber?: string
  firstName?: string
  lastName?: string
  title?: string
  companyName?: string
  rcIco?: string
  rc?: string
  ico?: string
  street?: string
  city?: string
  zip?: string
  contactPhone?: string
  contactEmail?: string
  firstNameCorresp?: string
  lastNameCorresp?: string
  titleCorresp?: string
  streetCorresp?: string
  cityCorresp?: string
  zipCorresp?: string
  vin?: string
  make?: string
  model?: string
  modelSpecification?: string
  volume?: string
  power?: string
  category?: string
  fuel?: string
  maxWeight?: string
  firstRegistrationYear?: string
  seatsCount?: string
  registrationMark?: string
  technicalCertificateNumber?: string
  prevInsuredFrom?: string
  prevInsuredTo?: string
  prevPovAmount?: string
  prevHavAmount?: string
  prevContractNumber?: string
  prevHavKodSazby?: string
  prevSpol?: string
  prevInsurerPov?: string
  prevInsurerHav?: string
  logText?: string
}

/////////////// Default values ////////////////////

export const InitialBaseAddressValues: BaseAddressDto = {
  postCode: '',
  country: `${Country.CZ}`,
  city: '',
}

export const InitialPersonValues: PersonDto = {
  address: InitialBaseAddressValues,
  type: Selection.Person,
  birthNumber: '',
  name: '',
  surname: '',
  isForeigner: false,
  nationalityId: undefined,
  idendificationNumber: '',
  companyName: '',
}

export const InitialPersonAdditionalData: PersonAdditionalData = {
  ...InitialPersonValues,
  mobilePhonePrefix: '+420',
  agreement: true,
  agreementOther: true,
} as PersonAdditionalData

export const InitialPolicyHolderAdditionalData: PolicyHolderAdditionalData = {
  person: InitialPersonAdditionalData,
  contactSameAs: Selection.PersonSameAsPolicyHolder,
  contact: InitialPersonAdditionalData,
  holderSameAs: Selection.PersonSameAsPolicyHolder,
  holder: InitialPersonAdditionalData,
  ownerSameAs: Selection.PersonSameAsPolicyHolder,
  owner: InitialPersonAdditionalData,
  insuredPersonSameAs: Selection.PersonSameAsPolicyHolder,
  insuredPerson: InitialPersonAdditionalData,
  showVinkulant: false,
  vinkulantExists: false,
  client: null,
}

export const InitialPolicyHolderValues: PolicyHolderDto = {
  benefits: [],
  bigRiskCompany: false,

  person: InitialPersonAdditionalData,

  holder: InitialPersonAdditionalData,
  holderSameAs: Selection.PersonSameAsPolicyHolder,

  owner: InitialPersonAdditionalData,
  ownerSameAs: Selection.PersonSameAsPolicyHolder,

  insuredPerson: InitialPersonAdditionalData,
  insuredPersonSameAs: Selection.PersonSameAsPolicyHolder,
}

export const InitialBaseParams = {
  insurers: [],
  addonInsurers: [],
  insurerDiscounts: [],
}

export const InitialBaseParamsValues: BaseParamsVehicleDto = {
  pov: [RiskVariant.WithoutPOV],
  hav: [RiskVariant.WithoutHAV],
  spol: [],
  ...InitialBaseParams,
}

export const InitialBaseParamsAddonValues: BaseParamsVehicleDto = {
  pov: [],
  hav: [],
  spol: [],
  insurers: [],
  addonInsurers: [],
  insurerDiscounts: [],
}

export const InitialGeneralParamsValues: GeneralParamsDto = {
  paramsId: null,
  clientId: null,
  insuranceStartDate: new Date(),
  insuranceEndDate: null,
  paymentFrequency: Selection.Yearly,
  paymentType: Selection.Bank,
  name: '',
  contractAcceptationType: 2,
  existsOffers: false,
  clonedFromParamsId: null,
  contractReplacementNumber: null,
}

export const InitialGeneralParamsPropertyValues: GeneralParamsDto = {
  paramsId: null,
  clientId: null,
  insuranceStartDate: customAddDays(new Date(), 1) /*.customAddDays(1)*/,
  paymentFrequency: Selection.Yearly,
  paymentType: Selection.Bank,
  name: '',
  contractAcceptationType: 2,
  existsOffers: false,
}

export const InitialVehicleValues: VehicleIdentificationDto & VehicleDto & VehiclePriceDto = {
  druh: 17,
  palivo: null,
  znacka: '',
  zakladniModel: '',
  model: '',
  puvod: 24,
  uzemniPlatnost: 210,
  objem: null,
  vykon1: null,
  vykon2: null,
  celkovaHmotnost: null,
  pocetMistSezeni: null,
  firstRegistrationDate: null,
  mileage: null,
  usage: 20,
  yearKm: '',
  purchaseDate: null,
  manufacturerWarrantyType: 0,
  warrantyStartDate: null,
  warrantyKmLimit: '',
  warrantyTermLimit: '',
  cisloTechnickehoPrukazu: '',
  cisloOrv: '',
  price: null,
  origInsuranceParamsPrice: null,
  priceType: null,
  cebicatVehicleId: null,
  priceIncludeVat: true,
  licensePlate: '',
  vin: '',
  vehicleLeasing: false,
  smluvniServis: false,
  prefLikvidace: false,
  autorizovanyServis: false,
  foreignRepairShop: false,
  equipment: [],
  security: [],
  extraEquipments: [],
  useVehicleEvaluation: null,
}

export const InitialPoleasVehicleValues: VehicleIdentificationDto & VehicleDto & VehiclePriceDto = {
  usage: 20,
  vehicleLeasing: false,
  smluvniServis: false,
  prefLikvidace: false,
  autorizovanyServis: false,
  foreignRepairShop: false,
  equipment: [],
  extraEquipments: [],
  security: [],
  price: null,
  origInsuranceParamsPrice: null,
  cebicatVehicleId: null,
  priceIncludeVat: true,
  useVehicleEvaluation: false,
  licensePlate: '',
  vin: '',
  uzemniPlatnost: 210,
}

export type LastWSProps = {
  paramsId: number
  step: number
  stepsStep?: string
}

export type GetMakersRequest = {
  categoryId: number
  fuelId: number
  useBrokerMakeModel: boolean
}

export type GetModelsBaseRequest = {
  maker: string
} & GetMakersRequest

export type GetModelsRequest = {
  modelBase?: string | null
} & GetModelsBaseRequest

export type GetWarningRequest = {
  insurers: number[]
}

export type Step1Warning = {
  message: string
  pov: string[]
  hav: string[]
  spol: string[]
  glass: string[]
}

export type Step1CategoryWarning = {
  message: string
  categories: string[]
}

export type InsuranceParamsLeasingDataDto = {
  leasingTypeId?: number | null
  leasingTerm?: number | null
  leasingContractNumber: string
  fixedPrice?: boolean | null
}

export type CalcAmountResponse = {
  amount?: number | null
  message: string
}

export type CalcMinAmountHouseholdRequest = {
  livingArea?: number | null
  equipment?: number | null
}

export type CalcMinAmountRealtyRequest = {
  builtUpArea?: number | null // podlahová plocha
  builtUpCadastralArea?: number | null // zastavěná plocha
  usefulArea?: number | null // celková užitná plocha
  realtyType?: number | null
  appartmentType?: number | null
  hasBasement?: boolean | null
  basementArea?: number | null
  floorCount?: number | null
  roofType?: number | null
  isRoofResidential?: boolean | null
  finishState?: number | null
  structuralType?: number | null
}

export type CalcAmountRealtyFlatRequest = {
  usefulArea?: number | null // užitná plocha
  addressCode?: number | null
  approvedYear?: number | null
  interiorState?: number | null
  flatDisposition?: number | null
  ownership?: number | null
  annuity?: number | null | null
  floor?: number | null
  topFloor?: boolean | null
  attic?: boolean | null
  loggiaArea?: number | null
  terraceArea?: number | null
  hasCellar?: boolean | null
  gardenArea?: number | null
  parkingType?: number | null
  numberOfParkings?: number | null
  isPanel?: boolean | null
  elevator?: boolean | null
}
